import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Input from '../../../../../../components/atoms/Fields/Input';
import { FieldMode } from '../../../../../../components/atoms/Fields/enums';
import Button from '../../../../../../components/atoms/Button';
import { ButtonMode } from '../../../../../../components/atoms/Button/enums';
import ProductThumbnail from '../../../../../../components/atoms/ProductThumbnail';
import PriceWithCurrency from '../../../../../../components/atoms/PriceWithCurrency';
import { IconSearch } from '../../../../../../assets/svg/icons';
import { useProductSearch } from '../../../../../../components/organisms/Modal/templates/PlaceOrderProductsModal/hooks';
import { createProductsTableItem } from '../../../../../../components/organisms/Modal/templates/PlaceOrderProductsModal/consts';
import { ProductsTableItem } from '../../OrderForm/types';
import {
  StyledSearchWrap,
  StyledSearchResults,
  StyledSearchResultRow,
  StyledSearchResultName,
  StyledSearchResultPrice,
  StyledSearchLoadMore,
  StyledEmptyHint,
} from '../styles';

interface InlineProductSearchProps {
  currency: string;
  entityId?: string;
  productsInOrder: ProductsTableItem[];
  onProductAdd: (product: ProductsTableItem) => void;
}

const InlineProductSearch: React.FC<InlineProductSearchProps> = ({
  currency,
  entityId,
  productsInOrder,
  onProductAdd,
}) => {
  const intl = useIntl();
  const { products, query, handleQueryChange, fetchMore, hasMore, isLoading } =
    useProductSearch(currency, entityId);

  return (
    <StyledSearchWrap>
      <Input
        value={query}
        onChange={handleQueryChange}
        mode={FieldMode.Secondary}
        placeholder={intl.formatMessage({
          id: 'search.searchProducts',
          defaultMessage: 'Search Products',
        })}
        isRounded
        prefixComponent={<IconSearch />}
      />
      {products.length > 0 ? (
        <StyledSearchResults>
          {products.map((product) => {
            const inOrder = productsInOrder.some(
              ({ idCol }) => idCol === product.key
            );

            return (
              <StyledSearchResultRow key={product.key}>
                <ProductThumbnail src={product.imageUrl} />
                <StyledSearchResultName>
                  {product.productName}
                </StyledSearchResultName>
                <StyledSearchResultPrice>
                  <PriceWithCurrency
                    price={product.price}
                    currency={product.currency}
                  />
                </StyledSearchResultPrice>
                <Button
                  type='button'
                  mode={ButtonMode.Secondary}
                  disabled={inOrder}
                  onClick={() => onProductAdd(createProductsTableItem(product))}
                >
                  {inOrder ? (
                    <FormattedMessage
                      id='orderWizard.added'
                      defaultMessage='Added'
                    />
                  ) : (
                    <FormattedMessage
                      id='forms.buttons.add'
                      defaultMessage='Add'
                    />
                  )}
                </Button>
              </StyledSearchResultRow>
            );
          })}
          {hasMore && (
            <StyledSearchLoadMore>
              <Button
                type='button'
                mode={ButtonMode.Secondary}
                onClick={() => fetchMore()}
              >
                <FormattedMessage
                  id='orderWizard.loadMore'
                  defaultMessage='Load more'
                />
              </Button>
            </StyledSearchLoadMore>
          )}
        </StyledSearchResults>
      ) : (
        !isLoading && (
          <StyledEmptyHint>
            <FormattedMessage
              id='search.noResults'
              defaultMessage='No results'
            />
          </StyledEmptyHint>
        )
      )}
    </StyledSearchWrap>
  );
};

export default InlineProductSearch;
